import axios from 'axios';
import { getMailPassword, clearAllMailPasswords } from './mailCredentials';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  // Default off: sending every localhost cookie on every request blows past the
  // dev-server header limit (431). SSO routes opt in below.
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Only these routes need the HttpOnly ATOZAS session cookie. */
const needsAtozasSessionCookie = (url) => {
  const path = (url || '').split('?')[0];
  return (
    path === '/auth/atozas/me' ||
    path === '/auth/atozas/mailbox' ||
    path === '/auth/atozas/username-check' ||
    path === '/auth/logout'
  );
};

const isAuthRoute = (url) => (url || '').split('?')[0].startsWith('/auth/');

const isLiveMailEndpoint = (url) => {
  const path = (url || '').split('?')[0];
  return (
    path.includes('/api/mail/server/') ||
    path === '/api/mail/inbox' ||
    path === '/api/mail/counts' ||
    path === '/api/mail/starred' ||
    path === '/api/mail/trash' ||
    path === '/api/mail/send' ||
    // Trash move / permanent delete / restore (e.g. /api/mail/trash/:uid/restore)
    /^\/api\/mail\/trash(\/|$)/.test(path) ||
    /^\/api\/mail\/restore\/[^/]+$/.test(path) ||
    /^\/api\/mail\/server\/trash(\/|$)/.test(path) ||
    /^\/api\/mail\/server\/message\/[^/]+$/.test(path) ||
    /^\/api\/mail\/delete\/[^/]+$/.test(path) ||
    path.startsWith('/api/duplicates') ||
    path.startsWith('/api/storage')
  );
};

const hasGroupSendPayload = (data) => {
  if (!data) return false;
  if (data instanceof FormData) return Boolean(data.get('groupId'));
  return Boolean(data.groupId);
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const mailPassword = getMailPassword();
    const requestUrl = config.url || '';

    if (needsAtozasSessionCookie(requestUrl)) {
      config.withCredentials = true;
    }

    // JWT is for /api/* only; attaching it to /auth/* inflates headers and is unused.
    if (token && !isAuthRoute(requestUrl)) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    if (mailPassword && isLiveMailEndpoint(requestUrl) && !hasGroupSendPayload(config.data)) {
      config.headers['x-mail-password'] = mailPassword;
    } else {
      delete config.headers['x-mail-password'];
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const code = error.response?.data?.code;
    const isMailboxAuthError = code === 'MAILBOX_AUTH_FAILED';
    const needsMailboxLogin =
      code === 'MAILBOX_CREDENTIALS_REQUIRED' ||
      (error.response?.status === 400 &&
        String(error.response?.data?.message || '').toLowerCase().includes('mailbox'));

    // Missing mailbox password: keep JWT session — do not force login page.
    if (needsMailboxLogin) {
      return Promise.reject(error);
    }

    // Wrong mailbox password: clear stored password and ask once more.
    if (isMailboxAuthError) {
      clearAllMailPasswords();
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !isMailboxAuthError) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearAllMailPasswords();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
